import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
} from "@mui/material";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function LimitingLearning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const pageUrl = "https://parishaudio.com/learning/limiting";
  const pageTitle = "Limiting in Music Production: What It Is and How It Works";
  const pageDescription =
    "Learn what a limiter does, how limiting prevents clipping, the difference between a limiter and compressor, and how limiting is used in mixing, mastering, and loudness control.";

  const schema = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        "@id": `${pageUrl}#article`,
        headline: pageTitle,
        description: pageDescription,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `${pageUrl}#webpage`,
        },
        url: pageUrl,
        inLanguage: "en-US",
        articleSection: "Audio Education",
        keywords: [
          "limiting",
          "audio limiter",
          "what does a limiter do",
          "limiter vs compressor",
          "mastering limiter",
          "peak limiting",
          "brickwall limiter",
          "true peak limiter",
        ],
        author: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
        },
        publisher: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
          logo: {
            "@type": "ImageObject",
            url: "https://parishaudio.com/assets/logo.png",
          },
        },
        image: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/RecordingStudioStockImage1.jpg",
        },
        about: [
          { "@type": "Thing", name: "Limiter" },
          { "@type": "Thing", name: "Peak control" },
          { "@type": "Thing", name: "Clipping prevention" },
          { "@type": "Thing", name: "Loudness control" },
          { "@type": "Thing", name: "Compression" },
          { "@type": "Thing", name: "Mastering" },
        ],
        isPartOf: {
          "@type": "CollectionPage",
          name: "Parish Audio Learning Center",
          url: "https://parishaudio.com/learning",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://parishaudio.com",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Learning Center",
            item: "https://parishaudio.com/learning",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Limiting",
            item: pageUrl,
          },
        ],
      },
    ],
    []
  );

  const quickFacts = [
    {
      title: "What it does",
      text: "A limiter controls peaks and prevents a signal from going above a set ceiling, helping avoid clipping and manage loudness.",
      icon: <VolumeUpRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Why it matters",
      text: "Limiting helps keep audio clean, controlled, and competitively loud without letting sudden transients overshoot.",
      icon: <SpeedRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where it is used",
      text: "Mastering chains, mix buses, drums, vocals, bass, broadcast audio, live sound, and anywhere peak control is important.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const limiterTypes = [
    {
      name: "Peak Limiter",
      description:
        "Designed to catch fast peaks and keep signals from exceeding a threshold or ceiling, often with very fast attack behavior.",
    },
    {
      name: "Brickwall Limiter",
      description:
        "A stricter form of limiting intended to stop peaks from going past the output ceiling, commonly used at the end of a mastering chain.",
    },
    {
      name: "Lookahead Limiter",
      description:
        "Uses a short preview of the signal so the limiter can react before a transient arrives, often producing cleaner and more transparent control.",
    },
    {
      name: "Program-Dependent Limiter",
      description:
        "Adapts its behavior to the material, which can sound smoother and more musical on complex mixes or changing dynamics.",
    },
    {
      name: "True Peak Limiter",
      description:
        "Designed to better control inter-sample peaks, which is especially useful for digital delivery formats and streaming platforms.",
    },
    {
      name: "Character / Color Limiter",
      description:
        "Adds some tonal coloration or saturation while limiting, trading strict transparency for a more stylized or punchy result.",
    },
  ];

  const limitingConcepts = [
    {
      name: "Threshold",
      description:
        "The level where limiting begins. When the signal tries to go above this point, the limiter turns it down.",
    },
    {
      name: "Ceiling / Output",
      description:
        "The maximum output level you want the signal to reach. This is often set slightly below 0 dBFS to reduce clipping risk.",
    },
    {
      name: "Gain Reduction",
      description:
        "The amount the limiter is turning the signal down in order to control peaks and stay under the ceiling.",
    },
    {
      name: "Transparency vs Loudness",
      description:
        "Heavier limiting can make a track louder, but too much can flatten transients, reduce punch, and make the audio feel strained.",
    },
  ];

  const controls = [
    {
      name: "Threshold",
      description:
        "Lowering the threshold makes the limiter work harder, causing more gain reduction and usually more apparent loudness control.",
    },
    {
      name: "Ceiling / Output Level",
      description:
        "Sets the maximum allowed output level. In mastering, this is often left a little below full scale for safety.",
    },
    {
      name: "Release",
      description:
        "Controls how quickly the limiter stops reducing gain after a peak passes. Too fast can sound edgy; too slow can sound squashed.",
    },
    {
      name: "Lookahead",
      description:
        "Lets the limiter react in advance to transients, which can improve control and reduce distortion at the cost of latency.",
    },
    {
      name: "Channel Link / Stereo Link",
      description:
        "Controls how the left and right channels influence each other, which can affect stereo stability and pumping behavior.",
    },
    {
      name: "Oversampling / True Peak",
      description:
        "Helps reduce digital artifacts and improves peak control, especially for final export and mastering applications.",
    },
  ];

  const commonUses = [
    "Preventing clipping on a mix bus or master bus",
    "Increasing perceived loudness in mastering",
    "Catching vocal peaks that jump out too aggressively",
    "Controlling drum transients without completely changing the groove",
    "Protecting broadcast, streaming, or live audio chains from overload",
    "Keeping exported audio within a safe digital ceiling",
  ];

  const bestPractices = [
    "Use your ears, not just the meter. A louder signal can sound impressive at first even when it is actually worse.",
    "Watch how much gain reduction is happening. A little limiting can be transparent, but too much often starts to flatten the mix.",
    "Set the output ceiling slightly below 0 dBFS when preparing digital releases.",
    "Do not expect a limiter to fix a bad mix. If the limiter is struggling, the real problem may be balance, EQ, or dynamics earlier in the chain.",
    "Pay attention to transients, punch, and vocal clarity. Those are often the first things to suffer when limiting becomes too heavy.",
  ];

  const relatedLinks = [
    {
      title: "Compression",
      to: "/learning/compression",
      description:
        "Learn how compression shapes dynamics more gradually and why limiting is often treated as a more extreme form of compression.",
    },
    {
      title: "Saturation",
      to: "/learning/saturation",
      description:
        "Explore how saturation adds harmonics and character, which is different from the strict peak control of limiting.",
    },
    {
      title: "Distortion",
      to: "/learning/distortion",
      description:
        "See how audible clipping and distortion differ from controlled limiting, even though both can reshape peaks.",
    },
    {
      title: "Equalization",
      to: "/learning/equalization",
      description:
        "Understand how EQ decisions can affect how hard a limiter has to work, especially in the low end and upper mids.",
    },
  ];

  const outerPanelSx = {
    width: "100%",
    minWidth: 0,
    borderRadius: "20px",
    overflow: "hidden",
    backgroundColor: style.color1,
    border: `1px solid ${style.color2}`,
    boxSizing: "border-box",
  };

  const subtlePanelSx = {
    borderRadius: "14px",
    backgroundColor: style.darkerPanel,
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "none",
  };

  return (
    <>
      <Seo
        title="Limiting | Parish Audio Learning"
        description={pageDescription}
        canonical={pageUrl}
        schema={schema}
      />

      <Box
        ref={topContainer}
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
          minHeight: height - 130,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
          <Stack spacing={{ xs: 2, md: 2.5 }}>
            <Paper
              elevation={0}
              sx={{
                ...outerPanelSx,
                minHeight: { xs: 260, md: 310 },
                display: "flex",
                alignItems: "center",
                backgroundImage:
                  'linear-gradient(90deg, rgba(8,12,18,0.84) 0%, rgba(8,12,18,0.74) 38%, rgba(8,12,18,0.42) 68%, rgba(8,12,18,0.22) 100%), url("/RecordingStudioStockImage1.jpg")',
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
                <Box sx={{ maxWidth: 760 }}>
                  <Stack spacing={1.25} alignItems="flex-start">
                    <Chip
                      label="Learning Center"
                      sx={{
                        borderRadius: "5px",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    />

                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: "2.2rem", md: "3.35rem" },
                        fontWeight: 800,
                        lineHeight: 1.02,
                        color: style.color4,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      Limiting
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 700,
                      }}
                    >
                      Limiting is one of the most important tools for controlling peaks,
                      preventing clipping, and managing loudness in digital audio. It is
                      commonly used in mixing and mastering to keep signals under
                      control, especially when a track needs to be loud without allowing
                      sudden transients to overshoot the maximum level.
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 700,
                      }}
                    >
                      In simple terms, a limiter is like a compressor with a very high
                      ratio. Once the signal reaches the threshold, the limiter acts
                      aggressively to stop it from going much higher. That makes it
                      extremely useful for peak control, final loudness shaping, and
                      protecting audio from clipping in digital systems.
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {quickFacts.map((item) => (
                <Paper key={item.title} elevation={0} sx={{ ...outerPanelSx }}>
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      {item.icon}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: style.color4,
                            mb: 0.5,
                            lineHeight: 1.2,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: style.color3,
                            lineHeight: 1.55,
                          }}
                        >
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <VolumeUpRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      What is a limiter?
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    A limiter is a dynamics processor designed to stop audio from
                    exceeding a chosen level. It reduces peaks once they cross a
                    threshold, helping keep the output under control. In digital
                    audio, this is especially important because signals that go above
                    the maximum level can clip, which often creates harsh unwanted
                    distortion.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    While compressors often shape dynamics in a more gradual or
                    flexible way, limiters are usually used when strict control is
                    needed. That is why limiting is so common on master buses,
                    broadcast chains, and other places where preventing overs is a
                    priority.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Limiting can be subtle or obvious. Light limiting may simply catch
                    the occasional transient. Heavy limiting can dramatically raise
                    average loudness, but it can also reduce punch, flatten dynamics,
                    and change the character of the material.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <SpeedRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      How limiting works
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {limitingConcepts.map((item) => (
                      <Paper
                        key={item.name}
                        elevation={0}
                        sx={{
                          ...subtlePanelSx,
                          p: 1.5,
                        }}
                      >
                        <Typography sx={{ color: accent, fontWeight: 700, mb: 0.4 }}>
                          {item.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: style.color3, lineHeight: 1.65 }}
                        >
                          {item.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Imagine a snare hit or vocal peak that suddenly jumps higher than
                    the rest of the signal. A limiter reacts to that spike and reduces
                    it so the output stays within bounds. If the limiter only catches
                    occasional peaks, the effect can be nearly invisible. If it is
                    working constantly, the sound often becomes denser, flatter, and
                    less dynamic.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Common types of limiters
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {limiterTypes.map((item) => (
                      <Paper
                        key={item.name}
                        elevation={0}
                        sx={{
                          ...subtlePanelSx,
                          p: 1.5,
                        }}
                      >
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.4 }}>
                          {item.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: style.color3, lineHeight: 1.65 }}
                        >
                          {item.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompareArrowsRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Limiter vs compressor
                    </Typography>
                  </Stack>

                  <Paper
                    elevation={0}
                    sx={{
                      ...subtlePanelSx,
                      p: 1.5,
                    }}
                  >
                    <Stack spacing={1.2}>
                      <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                        Similar family, different purpose
                      </Typography>

                      <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                        A limiter is usually considered a more extreme form of
                        compression. Both tools reduce dynamic range, but a limiter
                        tends to use a very high ratio and much stricter peak control.
                        Compressors are often chosen for shaping groove, tone, and
                        movement. Limiters are more often chosen for catching peaks
                        and setting a ceiling.
                      </Typography>

                      <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                        In practice, a compressor may be used earlier in the chain to
                        shape dynamics musically, while a limiter is placed later to
                        control the final peaks. The two often work together rather
                        than replacing each other.
                      </Typography>
                    </Stack>
                  </Paper>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TuneRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Common limiter controls
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {controls.map((item) => (
                      <Paper
                        key={item.name}
                        elevation={0}
                        sx={{
                          ...subtlePanelSx,
                          p: 1.5,
                        }}
                      >
                        <Stack direction="row" spacing={1.1} alignItems="flex-start">
                          <TuneRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                          <Box>
                            <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.4 }}>
                              {item.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: style.color3, lineHeight: 1.65 }}
                            >
                              {item.description}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LibraryMusicRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        How limiting is used in mixing and mastering
                      </Typography>
                    </Stack>

                    <Stack spacing={1.1}>
                      {commonUses.map((item) => (
                        <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                          <LibraryMusicRoundedIcon
                            sx={{ color: accent, mt: "2px", fontSize: 20, flexShrink: 0 }}
                          />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      On individual tracks, limiting can help catch unruly peaks that a
                      compressor is not quite handling the way you want. On a mix bus or
                      mastering chain, limiting is often used as the final stage that
                      keeps the whole track under control while increasing perceived
                      loudness. The key is to use only as much as the material can take
                      before it starts to lose life.
                    </Typography>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <WarningAmberRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        When limiting goes too far
                      </Typography>
                    </Stack>

                    <Stack spacing={1.1}>
                      {[
                        "Too much limiting can reduce punch, flatten transients, make cymbals or vocals sound strained, and cause the whole mix to feel smaller rather than bigger.",
                        "Heavy limiting may also create pumping, distortion, listener fatigue, or a loss of depth, especially if the low end keeps driving the limiter too hard.",
                        "Louder is not always better. A more open, dynamic mix often feels more powerful than one that has been pushed past its natural limit.",
                      ].map((item) => (
                        <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                          <WarningAmberRoundedIcon
                            sx={{ color: accent, mt: "2px", flexShrink: 0, fontSize: 20 }}
                          />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Best practices for using a limiter
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {bestPractices.map((item, index) => (
                      <Paper
                        key={index}
                        elevation={0}
                        sx={{
                          ...subtlePanelSx,
                          p: 1.5,
                        }}
                      >
                        <Stack direction="row" spacing={1.1} alignItems="flex-start">
                          <TipsAndUpdatesRoundedIcon
                            sx={{ color: accent, mt: "2px", flexShrink: 0 }}
                          />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Typography
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    Related learning topics
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "start",
                    }}
                  >
                    {relatedLinks.map((item) => (
                      <Box
                        key={item.title}
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "flex-start",
                        }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            ...subtlePanelSx,
                            p: 1.5,
                            width: "100%",
                            minHeight: "75%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}
                            >
                              {item.title}
                            </Typography>
                            <Typography
                              sx={{ color: style.color3, lineHeight: 1.75, mb: 2 }}
                            >
                              {item.description}
                            </Typography>
                          </Box>

                          <Box>
                            <Button
                              component={RouterLink}
                              to={item.to}
                              sx={{
                                borderRadius: "5px",
                                textTransform: "none",
                                fontWeight: 700,
                                color: style.color4,
                                backgroundColor: "rgba(255,255,255,0.05)",
                                border: `1px solid ${accent}`,
                                "&:hover": {
                                  borderColor: style.color4,
                                  backgroundColor: "rgba(255,255,255,0.10)",
                                },
                              }}
                            >
                              Read More
                            </Button>
                          </Box>
                        </Paper>
                      </Box>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Final thoughts
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                    Limiting is one of the final safeguards in modern audio
                    production. It helps control peaks, prevent clipping, and raise
                    loudness when needed. Used carefully, it can make a mix feel
                    polished and controlled without drawing attention to itself.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                    The key is balance. A limiter can protect your audio and improve
                    presentation, but it can also damage punch and musicality when
                    pushed too hard. Learning how much limiting a song can handle is
                    one of the most important judgment calls in mixing and mastering.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
                <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.7 }}>
                  * We are not affiliated with any brands or artists mentioned on this site. *
                </Typography>
              </Box>
            </Paper>
          </Stack>
        </Box>
      </Box>
    </>
  );
}